/**
 * Gate Fee API Service
 *
 * REST wrapper for /api/gate-fees. Only the session half lives here — the
 * completion call is made by the Chrome extension from the eModal tab, because
 * that is the only place the receipt exists.
 */

import api from './api';

/** One eModal purchase we already have on file for this order. */
export interface GateFeePaidRecord {
  amount: number;
  paidAt: string;
  purchaseNumber: string | null;
  facilityCode: string | null;
}

export interface GateFeeSession {
  sessionId: string;
  /** eModal's fee-payment landing page. */
  url: string;
  orderId: number;
  orderNumber: string;
  containerNumber: string;
  /** eModal returns nothing at all if this radio is wrong, so it is not optional. */
  tradeType: 'IMPORT' | 'EXPORT';
  portId: number | null;
  terminalCode: string | null;
  terminalName: string | null;
  alreadyPaid: GateFeePaidRecord[];
  /** 批量时这一趟覆盖的全部柜子；单柜时不返回。 */
  containers?: Array<{
    orderId: number;
    orderNumber: string;
    containerNumber: string;
    tradeType: 'IMPORT' | 'EXPORT';
    terminalCode: string | null;
    terminalName: string | null;
  }>;
  /** 没柜号之类、被剔掉的那些。 */
  skipped?: Array<{ taskId: number; reason: string }>;
}

/** 批量弹窗里的一行。 */
export interface GateFeeCandidate {
  taskId: number;
  taskNumber: string;
  orderId: number;
  containerNumber: string;
  tradeType: 'IMPORT' | 'EXPORT';
  terminalCode: string | null;
  terminalName: string | null;
  /** 下单方（Ord. Party）—— 和派单列表那一列同一个口径。 */
  clientName: string | null;
  status: string;
  /** 不为 null 就不给勾，并把原因写在行上。 */
  blockedReason: string | null;
}

export interface GateFeeCandidates {
  anchorTaskId: number;
  maxPerBatch: number;
  truncated: boolean;
  candidates: GateFeeCandidate[];
}

export const gateFeeApi = {
  /** 能和这一只柜一起付的其它柜。 */
  async getCandidates(taskId: number | string): Promise<GateFeeCandidates> {
    const response = await api.get(`/gate-fees/candidates?taskId=${encodeURIComponent(String(taskId))}`);
    return response.data as GateFeeCandidates;
  },

  /**
   * Ask the backend what the extension should look up on eModal. Pass whichever
   * id the calling surface holds — task rows have a task, the pricing panel has
   * an order.
   */
  async createSession(params: { taskId?: number | string; orderId?: number; taskIds?: number[] }): Promise<GateFeeSession> {
    const response = await api.post('/gate-fees/session', params);
    return response.data as GateFeeSession;
  },
};

export default gateFeeApi;
